import type { IApply } from "@bbj-types/apply";
import type { IOffer } from "@bbj-types/offer";
import type { ObjectId } from "@bbj-types/utils";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { ApplyActions } from "./actions";
import { type IEnterpriseBody, ModalOfferProvider, type TModalOfferMode } from "./context";
import { OfferForm } from "./form";
import { OfferFormApplicationFollowUp } from "./formApplicationFollowUp";
import { type IApplyCreate, useModalOffer } from "./logics";

export interface IModalOfferDisableFields {
  applyStatus?: boolean;
}

interface IModalOfferProps<IsApply extends boolean | undefined> {
  open: boolean;
  title?: string;
  offer?: Partial<IOffer>;
  candidateId?: ObjectId;
  onConfirm: (result: IsApply extends true ? IApply : IOffer) => void;
  onCancel: () => void;
  isApply?: IsApply;
  applyFollowUp?: boolean;
  defaultApplyValues?: IsApply extends true ? Partial<IApplyCreate> : never;
  initEnterprise?: IEnterpriseBody;
  managementProcess?: IsApply extends true ? ObjectId : undefined;
  disabledFields?: IModalOfferDisableFields;
  mode?: TModalOfferMode;
  applyId?: ObjectId;
}

export function ModalOffer<IsApply extends boolean | undefined = undefined>({
  open = false,
  title: titleProp,
  offer,
  initEnterprise,
  candidateId,
  onConfirm,
  onCancel,
  isApply,
  applyFollowUp,
  managementProcess,
  defaultApplyValues,
  disabledFields,
  mode = "create",
  applyId,
  ...props
}: IModalOfferProps<IsApply>) {
  const title = titleProp ?? (mode === "editApply" ? "Modification de la candidature" : "Nouvelle offre d'emploi");
  const contextValues = useModalOffer(
    open,
    offer,
    onConfirm,
    onCancel,
    isApply,
    defaultApplyValues,
    candidateId,
    managementProcess,
    initEnterprise,
    applyFollowUp,
    mode,
    applyId
  );

  return (
    <ModalOfferProvider {...contextValues} disabledFields={disabledFields}>
      <Dialog open={open} fullWidth maxWidth="sm" disableEnforceFocus {...props}>
        <DialogTitle component={Typography} variant="h4" sx={{ p: 3 }}>
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {title}
            <IconButton
              aria-label="close"
              size="xs"
              onClick={contextValues.onCancel}
              sx={{ color: (theme) => theme.palette.black.dark }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {applyFollowUp ? (
            <OfferFormApplicationFollowUp />
          ) : (
            <OfferForm isApply={isApply} disableEtp={!!initEnterprise} />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <ApplyActions isApply={isApply} />
        </DialogActions>
      </Dialog>
    </ModalOfferProvider>
  );
}
